import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, MessageCircleHeart, ChevronDown, ChevronUp, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/site/StatusBadge";
import { toast } from "sonner";

const STATUS_FLOW = [
  ["ACTIVE", "Still missing"],
  ["FOUND_SAFE", "Found safe"],
  ["LOCATED", "Located (details private)"],
  ["REUNITED", "Reunited with family"],
  ["CLOSED", "Closed"],
  ["WITHDRAWN", "Withdrawn"],
];

/** /app/missing — the family administrator's dashboard for their cases. */
export default function MyCases() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tips, setTips] = useState({}); // caseRef updates when expanded
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/missing/mine");
      setCases(data.cases);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function toggleTips(c) {
    if (open === c.case_id) {
      setOpen(null);
      return;
    }
    setOpen(c.case_id);
    try {
      // family admin view returns private updates too
      const { data } = await api.get(`/v1/missing/${c.case_id}`);
      setTips((t) => ({ ...t, [c.case_id]: (data.updates || []).filter((u) => !u.is_public) }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function setStatus(c, status) {
    try {
      await api.patch(`/v1/missing/${c.case_id}`, { status });
      toast.success("Status updated — the timeline has been marked.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <section className="mx-auto max-w-4xl px-6 md:px-10 pt-8 pb-24" data-testid="my-cases-page">
      <header className="mb-10">
        <div className="overline mb-2">§ Missing &amp; Found</div>
        <h1 className="heritage-title text-4xl md:text-5xl leading-tight">Cases you keep</h1>
        <p className="mt-3 text-[color:var(--ink-soft)] max-w-xl">
          You are the administrator of these cases — new information arrives here privately, and you control the record.
        </p>
        <Link to="/missing/report" className="btn-rust mt-6 inline-flex" data-testid="my-cases-new">
          <Plus size={15} /> Open a new case
        </Link>
      </header>

      {loading ? (
        <div className="py-16 text-center text-[color:var(--ink-soft)]">Opening your ledger…</div>
      ) : cases.length === 0 ? (
        <div className="border border-dashed border-[color:var(--ink-line-strong)] p-12 text-center">
          <p className="font-italic-serif italic text-xl text-[color:var(--ink-soft)]">
            No cases — may it stay that way. If the day comes, we are here.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="my-cases-list">
          {cases.map((c) => (
            <div key={c.case_id} className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-6" data-testid={`my-case-${c.case_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={c.status} />
                    <span className="numeric-index text-[color:var(--ink-soft)]">{c.case_id}</span>
                    {c.pending_tips > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-[color:var(--rust)]" data-testid={`my-case-tips-badge-${c.case_id}`}>
                        <MessageCircleHeart size={12} /> {c.pending_tips} new {c.pending_tips === 1 ? "message" : "messages"}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 font-serif-display text-2xl">{c.name}</h3>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-[color:var(--ink-soft)]">
                    <MapPin size={11} />
                    {[c.loc?.city, c.loc?.region, c.loc?.country].filter(Boolean).join(", ")} · last seen {c.last_seen_date}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Link to={`/missing/case/${c.case_id}`} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`my-case-view-${c.case_id}`}>
                    View public page
                  </Link>
                  <button onClick={() => toggleTips(c)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`my-case-inbox-${c.case_id}`}>
                    {open === c.case_id ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Private inbox
                  </button>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-[color:var(--sepia)]/20 pt-4">
                <span className="overline text-[10px]">Set status</span>
                <select
                  data-testid={`my-case-status-${c.case_id}`}
                  value={c.status}
                  onChange={(e) => setStatus(c, e.target.value)}
                  className="bg-transparent border-b border-[color:var(--sepia)]/50 py-1 text-sm focus:outline-none"
                >
                  {STATUS_FLOW.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
                <span className="text-[11px] text-[color:var(--ink-soft)]">
                  A verified-deceased status can only be set by platform moderators, after authoritative confirmation.
                </span>
              </div>

              {open === c.case_id && (
                <div className="mt-5 border-t border-[color:var(--sepia)]/20 pt-4" data-testid={`my-case-inbox-panel-${c.case_id}`}>
                  <div className="overline text-[10px] mb-3">Private messages &amp; reports</div>
                  {(tips[c.case_id] || []).length === 0 ? (
                    <p className="text-sm font-italic-serif italic text-[color:var(--ink-soft)]">Nothing yet — the inbox is quiet.</p>
                  ) : (
                    <ol className="space-y-4">
                      {tips[c.case_id].map((t) => (
                        <li key={t.id} className="border-l-2 border-[color:var(--rust)]/60 pl-4" data-testid={`my-case-tip-${t.id}`}>
                          <div className="numeric-index text-[color:var(--ink-soft)]">
                            {t.kind === "report" ? "A moderator report" : "Someone with information"} ·{" "}
                            {t.created_at && new Date(t.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                          </div>
                          <p className="mt-1 text-[15px] leading-[1.6]">{t.body}</p>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
